import React, { useMemo } from "react";
import {
    Box,
    Checkbox,
    Chip,
    FormControl,
    MenuItem,
    Select,
    Stack,
    Typography,
} from "@mui/material";

import {
    BRAND_CHECKBOX_SX,
    getProficiencySelectSx,
} from "./batchAssessmentColors";

const PROFICIENCY_OPTIONS = [
    "",
    "Emerging",
    "Developing",
    "Proficient",
    "Extending",
];

const BatchDOKSection = ({
    dokLevel,
    skills = [],
    studentState,
    onSkillChange,
    onProficiencyChange,
}) => {
    const dokSkills = useMemo(
        () =>
            skills.filter(
                (skill) =>
                    skill.dok_level?.level === dokLevel.level
            ),
        [skills, dokLevel.level]
    );

    const selectedCount = dokSkills.filter(
        (skill) =>
            studentState?.entries?.[skill.id] === true
    ).length;

    const proficiency =
        studentState?.dokProficiency?.[
            dokLevel.level
        ] || "";

    const requiresProficiency =
        selectedCount > 0 && !proficiency;

    const requiresSkill =
        selectedCount === 0 && Boolean(proficiency);

    return (
        <Box
            sx={{
                border: "1px solid",
                borderColor:
                    requiresProficiency || requiresSkill
                        ? "warning.main"
                        : "divider",
                borderRadius: 2.5,
                overflow: "hidden",
            }}
        >
            {/* Header */}
            <Stack
                direction={{
                    xs: "column",
                    sm: "row",
                }}
                justifyContent="space-between"
                alignItems={{
                    xs: "stretch",
                    sm: "center",
                }}
                spacing={1.5}
                sx={{
                    px: 2,
                    py: 1.5,
                    backgroundColor: "#F7F7F2",
                }}
            >
                <Box>
                    <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                    >
                        <Chip
                            label={`DOK ${dokLevel.level}`}
                            size="small"
                            sx={{
                                fontWeight: 700,
                            }}
                        />

                        <Typography
                            fontWeight={700}
                            fontSize="0.95rem"
                        >
                            {dokLevel.description}
                        </Typography>
                    </Stack>

                    <Typography
                        variant="caption"
                        color="text.secondary"
                    >
                        {selectedCount} of{" "}
                        {dokSkills.length} Skills demonstrated
                    </Typography>
                </Box>

                <FormControl
                    size="small"
                    sx={{
                        minWidth: {
                            xs: "100%",
                            sm: 170,
                        },
                    }}
                >
                    <Select
                        value={proficiency}
                        displayEmpty
                        onChange={(event) =>
                            onProficiencyChange(
                                dokLevel.level,
                                event.target.value
                            )
                        }
                        sx={getProficiencySelectSx(
                            proficiency
                        )}
                    >
                        <MenuItem value="">
                            <em>Select proficiency</em>
                        </MenuItem>

                        {PROFICIENCY_OPTIONS
                            .filter(Boolean)
                            .map((option) => (
                                <MenuItem
                                    key={option}
                                    value={option}
                                >
                                    {option}
                                </MenuItem>
                            ))}
                    </Select>
                </FormControl>
            </Stack>

            {/* Skills */}
            <Stack
                spacing={0}
                divider={
                    <Box
                        sx={{
                            borderBottom: "1px solid",
                            borderColor: "divider",
                        }}
                    />
                }
            >
                {dokSkills.map((skill) => {
                    const checked =
                        studentState?.entries?.[
                            skill.id
                        ] === true;

                    return (
                        <Box
                            key={skill.id}
                            sx={{
                                px: 2,
                                py: 1.25,
                            }}
                        >
                            <Stack
                                direction="row"
                                spacing={1}
                                alignItems="flex-start"
                            >
                                <Checkbox
                                    checked={checked}
                                    onChange={(event) =>
                                        onSkillChange(
                                            skill.id,
                                            event.target.checked
                                        )
                                    }
                                    sx={{
                                        mt: -0.5,
                                        ...BRAND_CHECKBOX_SX,
                                    }}
                                />

                                <Typography
                                    variant="body2"
                                    sx={{
                                        pt: 0.45,
                                        lineHeight: 1.55,
                                    }}
                                >
                                    {skill.skill_description}
                                </Typography>
                            </Stack>
                        </Box>
                    );
                })}
            </Stack>

            {/* Validation */}
            {requiresProficiency && (
                <Box
                    sx={{
                        px: 2,
                        py: 1,
                        backgroundColor:
                            "warning.50",
                    }}
                >
                    <Typography
                        variant="caption"
                        color="warning.dark"
                        fontWeight={600}
                    >
                        Required to save: select a
                        proficiency for DOK{" "}
                        {dokLevel.level}.
                    </Typography>
                </Box>
            )}

            {requiresSkill && (
                <Box
                    sx={{
                        px: 2,
                        py: 1,
                        backgroundColor:
                            "warning.50",
                    }}
                >
                    <Typography
                        variant="caption"
                        color="warning.dark"
                        fontWeight={600}
                    >
                        DOK {dokLevel.level}:{" "}
                        {dokLevel.description}
                        {" "}proficiency selected
                        but no Skill applied.
                        Select at least one Skill,
                        or clear the proficiency.
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default BatchDOKSection;